<template>
  <div class="panel-content-wrapper" v-if="!isCampaignPending">
    <p class="title">{{ $t("app.campaign.single.addTalent", { count: 2 }) }}</p>
    <div class="separator"></div>
    <div class="spacing">
      <p class="m-text bold">{{ $t("app.campaign.single.inviteByEmail") }}</p>
      <VForm
        :submitButton="{
          label: $t('app.campaign.single.addTalent'),
          class: 'primary sm',
          hotDisabled: true,
        }"
        :externalRule="state.email !== '' && state.firstname !== '' && state.lastname !== ''"
        :displayErrors="false"
        :callback="() => {
          addParticipantToList();
          addParticipantsToCampaign();
        }"
        :clean="true"
      >
        <template #form-body="{ errors }">
          <VField
            class="sm xs-text"
            :label="$t('app.campaign.single.talentEmail')"
            name="add-candidate-email"
            :placeholder="$t('app.campaign.single.emailPlaceholder')"
            type="email"
            v-model="state.email"
            :class="{
              invalid: Object.keys(errors).includes('add-candidate-email'),
            }"
            :rules="{
              email: true,
              not_included: state.newParticipants.map((elt) => elt.email),
            }"
            :displayError="true"
          />
          <div class="form">
            <VField
              class="sm xs-text"
              :label="$t('app.campaign.single.talentFirstName') + '*'"
              placeholder="John"
              name="add-candidate-firstname"
              v-model="state.firstname"
              rules="textOnly"
              :class="{
                invalid: Object.keys(errors).includes('add-candidate-firstname'),
              }"
              :displayError="true"
            />
            <VField
              class="sm xs-text"
              :label="$t('app.campaign.single.talentLastName') + '*'"
              name="add-candidate-lastname"
              placeholder="Doe"
              v-model="state.lastname"
              rules="textOnly"
              :class="{
                invalid: Object.keys(errors).includes('add-candidate-lastname'),
              }"
              :displayError="true"
            />
          </div>
        </template>
      </VForm>
    </div>
    <div class="separator"></div>
    <div class="invite-by-link">
      <div class="inline">
        <p class="m-text bold">{{ $t("app.campaign.single.inviteByLink") }}</p>
        <div class="toggle-container">
          <div class="toggle" @click="showCampaignLinkModal">
            <input type="checkbox" :checked="state.linkActivated" />
            <label />
          </div>
        </div>
      </div>
      <transition name="fade">
        <div class="spacing" v-if="state.linkActivated && publicInvitation?.id">
          <InputCopy class="link-title sm xs-text" :label="$t('app.campaign.single.campaignLink')" :value="campaignShareLink" />
          <p class="subtitle">{{ $t('app.campaign.single.talentActivationOptions') }}</p>
          <div class="radio-group">
            <label :class="['radio-option', 'radio-option-left', state.autoActivateCandidate ? 'radio-option-selected' : '']">
              <div class="radio-header">
                <input class="auto-radio-button-dot" type="radio" :checked="state.autoActivateCandidate" @change="handleRadioChange(true)" />
                <div class="radio-option-title s-text bold">{{ $t("app.campaign.single.autoActivate") }}</div>
              </div>
              <div>
                <div class="radio-option-text xs-text">
                  {{ $t("app.campaign.single.autoActivateDesc") }}
                </div>
              </div>
            </label>
            <label :class="['radio-option', !state.autoActivateCandidate ? 'radio-option-selected' : '']">
              <div class="radio-header">
                <input class="auto-radio-button-dot" type="radio" :checked="!state.autoActivateCandidate" @change="handleRadioChange(false)" />
                <div class="radio-option-title s-text bold">{{ $t("app.campaign.single.manualActivate") }}</div>
              </div>
              <div>
                <div class="radio-option-text xs-text">
                  {{ $t("app.campaign.single.manualActivateDesc") }}
                </div>
              </div>
            </label>
          </div>
        </div>
      </transition>
    </div>
    <div class="separator"></div>
    <div class="csv-import-section">
      <p class="m-text bold">{{ $t("app.campaign.single.importTalentsCSV") }}
        <span>
          <InfoIcon style="width: 16px; margin-bottom: -1; margin-left: 6px;" v-tooltip="$t('app.campaign.single.importTalentsInfo')"></InfoIcon>
        </span>
      </p>
      <div class="dragzone-wrapper">
        <Dragzone @fileSelected="onFileUpload" @fileRemoved="onFileRemoved" />
      </div>
      <Button class="primary sm" :disabled="!state.newParticipants.length || !state.isFileLoaded" :label="$t('app.campaign.single.addTalent', { count: state.newParticipants.length || 1})"></Button>
    </div>
    <ActionModal modal="activate" :data="state.participants" :isOpen="state.showActivateModal" @close="cancelActivateModal" @confirm="cancelActivateModal" />
    <ActionModal modal="deactivateLink" :data="[]" :isOpen="state.showLinkModal" @close="state.showLinkModal = false" @confirm="enableCampaignLink" />
  </div>
</template>

<script setup lang="ts">
import { reactive } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import * as Xlsx from "xlsx";
import Papa from "papaparse";

import ActionModal from "@/components/modals/app/actionModal/ActionModal.vue";
import Dragzone from "@/components/global/Dragzone.vue";
import Button from "@/components/global/Button.vue";
import InfoIcon from '@/assets/images/icon/campaign/result_panel/info.svg?component';

import { Participant } from "@/types/participant";
import { useAppStore } from "@/stores/app.js";

import { useCampaignPublicInvitation, useDisablePublicInvitation, useEnablePublicInvitation, useInviteInCampaign } from "@/queries/invitation.query";
import { useActivateParticipations, useParticipations } from "@/queries/participant.query";
import { useCampaign, useEditCampaign } from "@/queries/campaigns.query";
import { useOrganisationCredits } from "@/queries/organisation.query";

const route = useRoute();
const router = useRouter();
const toast = useToast();
const { t } = useI18n();

// Init stores
const appStore = useAppStore();

const { data: credits, isPending: isCreditsPending } = useOrganisationCredits();
const { data: campaign, isPending: isCampaignPending} = useCampaign();
const { data: publicInvitation, isPending: isPublicInvitationPending } = useCampaignPublicInvitation();
const { mutate: editCampaign } = useEditCampaign();

const { data: participations, isPending } = useParticipations();
const { mutateAsync: activateParticipations } = useActivateParticipations();
const { mutateAsync: inviteInCampaign } = useInviteInCampaign();
const { mutate: enablePublicInvitation } = useEnablePublicInvitation();
const { mutate: disablePublicInvitation } = useDisablePublicInvitation();

interface CandidateRow {
  email: string,
  first_name: string,
  last_name: string,
}

const emailMapping = [
  "email",
  "courriel",
  "mail",
  "e-mail",
  'mél',
];

const firstNameMappping = [
  "first name",
  "prénom",
  "firstname",
  "first_name"
];

const lastNameMappping = [
  "last name",
  "nom",
  "lastname",
  "last_name"
];

const campaignShareLink = computed(() => {
  if (isCampaignPending.value || !campaign.value || !publicInvitation?.value?.id)
    return null;

  return `${window.location.host.replace("app", "game")}/join?invitation_id=${
    publicInvitation.value.id
  }`;
});

const state = reactive({
  email: "",
  firstname: "",
  lastname: "",
  isLoading: false,
  autoActivateCandidate: campaign.value?.auto_activate,
  linkActivated: false,
  showActivateModal: false,
  showLinkModal: false,
  newParticipants: [],
  participants: [],
  isFileLoaded: false,
});

function onFileUpload(file: File) {
  state.isFileLoaded = true;
  const fileExtension = file.name.split('.').pop().toLowerCase();

  if (fileExtension === 'csv') {
    parseCsvAndMap(file, addCandidatesToList);
  } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
    parseExcelAndMap(file, addCandidatesToList);
  } else {
    toast.error(t('other.error.ERROR_UNSUPPORTED_FORMAT'));
  }
}

function onFileRemoved(file: File) {
  state.isFileLoaded = false;
  const fileExtension = file.name.split('.').pop().toLowerCase();

  if (fileExtension === 'csv') {
    parseCsvAndMap(file, removeCandidatesFromList);
  } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
    parseExcelAndMap(file, removeCandidatesFromList);
  } else {
    toast.error(t('other.error.ERROR_UNSUPPORTED_FORMAT'));
  }
}

function addCandidatesToList(candidates: CandidateRow[]) {
  for (const row of candidates) {
    if (state.newParticipants.some((elt) => elt.email === row.email)) {
      continue;
    }

    state.newParticipants.push({
      email: row.email,
      first_name: row.first_name,
      last_name: row.last_name,
      activeAssessment: true,
    });
  }
}

function removeCandidatesFromList(candidates: CandidateRow[]) {
  for (const row of candidates) {
    state.newParticipants = state.newParticipants.filter(elt => elt.email !== row.email);
  }
}

function parseCsvAndMap(file: File, callback: (candidates: CandidateRow[]) => void) {
  Papa.parse(file, {
    header: true,       // convert rows to objects
    skipEmptyLines: true,
    transformHeader: (header: string) => { // normalize header names
      const trimmedHeader = header.toLowerCase().trim();
      if (emailMapping.includes(trimmedHeader)) {
        return "email";
      } else if (firstNameMappping.includes(trimmedHeader)) {
        return "first_name";
      } else if (lastNameMappping.includes(trimmedHeader)) {
        return "last_name";
      }
      return trimmedHeader;
    },
    complete: (results) => {
      if (results.data[0]["email"] === undefined || results.data[0]["first_name"] === undefined || results.data[0]["last_name"] === undefined) {
        toast.error(t('app.campaign.single.importTalentsInfo'));
        return
      }
      callback(results.data);
    },
    error: (err) => {
      console.error('CSV Error:', err);
    }
  })
}

async function parseExcelAndMap(file: File, callback: (candidates: CandidateRow[]) => void) {
    const data = await file.arrayBuffer();
    const workbook = Xlsx.read(data, { type: 'array' });

    // Get first sheet
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];

    // Convert to JSON
    const rows: any[] = Xlsx.utils.sheet_to_json(worksheet, {
      defval: null,
      header: 1,
    });

    if (!rows.length) return;
    const originalHeaders: string[] = rows[0];
    const transformedHeaders = originalHeaders.map((header: string) => { // normalize header names
      const trimmedHeader = header.toLowerCase().trim();
      if (emailMapping.includes(trimmedHeader)) {
        return "email";
      } else if (firstNameMappping.includes(trimmedHeader)) {
        return "first_name";
      } else if (lastNameMappping.includes(trimmedHeader)) {
        return "last_name";
      }
      return trimmedHeader;
    });
    const candidates: CandidateRow[] = rows.slice(1).map(row => {
      let obj: CandidateRow = {email: "", first_name: "", last_name: ""};
      transformedHeaders.forEach((header, i) => {
        obj[header] = row[i];
      });
      return obj;
    });
    callback(candidates);
}

function addParticipantToList() {
  state.newParticipants.unshift({
    email: state.email,
    first_name: state.firstname,
    last_name: state.lastname,
    activeAssessment: true,
  });
}

function removeFromList(email) {
  const entry = state.newParticipants.findIndex((elt) => elt.email === email);

  state.newParticipants.splice(entry, 1);
}

async function activateAllParticipants(allParticipants: Participant[]) {
  const participantIds = allParticipants.map((elt) => elt.participant_id);

  if (participantIds.length > 0) {
    // avoid error if participants already added
    await activateParticipations({
      context: {
        organisationId: route.params.companyId as string,
        spaceId: route.params.spaceId as string,
        campaignId: route.params.campaignId as string,
      },
      participantIds,
    });

  }
}

async function addParticipantsToCampaign() {
  state.isLoading = true;

  // If not enough credits do not create participants
  // Just display warning message
  if (state.newParticipants.length > (credits.value?.available ?? 0)) {
    state.participants = state.newParticipants;
    state.showActivateModal = true;
    state.isLoading = false;
    return;
  }

  // Remove duplicates
  state.newParticipants = state.newParticipants.filter((elt) => !participations.value.some((participant) => (participant?.candidate?.email === elt.email || participant?.pending_email === elt.email)));
  if (state.newParticipants.length === 0) return;

  const promises: Promise<void>[] = [];
  state.newParticipants.forEach((participant) => {
    const p = inviteInCampaign({
      campaignId: route.params.campaignId as string,
      email: participant.email,
      firstName: participant.first_name,
      lastName: participant.last_name,
    });
    promises.push(p);
  });

  try {
    await Promise.all(promises);
    toast.success(t("app.campaign.single.talentsAdded", { count: promises.length }));
    // await activateAllParticipants(data.participants);
    goBack();
  } catch (error) {
    console.error(error);
  } finally {
    appStore.activatedTableHasNews[`${route.params.campaignId}`] = true;
    state.isLoading = false;
  }
}

function cancelActivateModal() {
  state.showActivateModal = false;
}

function goBack() {
  appStore.currentCampaignTab = "Overview";

  state.newParticipants = [];

  router.push({ name: "campaign" });
}

function showCampaignLinkModal(event) {
  if (state.linkActivated) {
    event.preventDefault();
    state.showLinkModal = true;
  } else {
    enableCampaignLink();
  }
}

async function enableCampaignLink() {
  state.linkActivated = !state.linkActivated;
  state.isLoading = true;
  if (!state.linkActivated && publicInvitation?.value?.id) {
    disablePublicInvitation({ invitationId: publicInvitation?.value?.id });
  } else if (state.linkActivated && !publicInvitation?.value?.id) {
    enablePublicInvitation({ campaignId: route.params.campaignId as string });
  }
  state.isLoading = false;
  // state.showLink = state.linkActivated;
  // console.log(state.showLink);

  state.showLinkModal = false;
}

async function handleRadioChange(value) {
  state.autoActivateCandidate = value;

  editCampaign({
    context: {
      organisationId: route.params.companyId as string,
      spaceId: route.params.spaceId as string,
      campaignId: campaign.value.id,
    },
    campaign: campaign.value,
    auto_activate: state.autoActivateCandidate,
  });
}

watch(publicInvitation, () => {
  state.linkActivated = !!publicInvitation?.value?.id;
  state.autoActivateCandidate = !publicInvitation?.value?.requires_approval;
});

onMounted(() => {
  state.linkActivated = !!publicInvitation?.value?.id;
  state.autoActivateCandidate = !publicInvitation?.value?.requires_approval;
});
</script>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.panel-content-wrapper {
  max-width: 40vw;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 1.5rem;

  .separator {
    width: 100%;
    height: 1px;
    background-color: var(--light-grey-color);
  }

  .title {
    font-size: 20px;
    font-weight: 700;
  }

  .form {
    display: flex;
    flex-direction: row;
    gap: 8px;

    .input-wrapper {
      flex: 1;
    }
  }

  .spacing {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .invite-by-link {
    display: flex;
    flex-direction: column;
    gap: 12px;

    .inline {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .subtitle {
      font-size: 12px;
      color: var(--label-color);
    }

    .radio-group {
      display: flex;
      align-items: stretch;
      gap: 8px;

      .radio-header {
        display: flex;
        gap: 8px;
        align-items: center;

        input {
          margin-top: 0;
        }
      }

      .radio-option {
        display: flex;
        flex-direction: column;
        gap: 8px;

        flex: 1;
        padding: 8px;
        border-radius: 4px;
        border: 1px solid var(--light-grey-color);
      }
    }
  }
}

.csv-import-section {
  display: flex;
  flex-direction: column;
  gap: 12px;

  .dragzone-wrapper {
    width: 100%;
  }
}
</style>